import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import VideoPlayer from '../../components/student/VideoPlayer.jsx';
import {
  getCourse,
  saveVideoProgress,
  getVideoProgress,
  getVideoLessonProgress,
  markVideoLessonComplete
} from '../../services/api';
import './CoursePlayer.css';

const CoursePlayer = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const API_BASE = process.env.REACT_APP_API_URL || 'https://api.azalega-academy.com';
  // Remove /api/v1 suffix if present to get base URL for assets
  const ASSET_BASE = API_BASE.endsWith('/api/v1') ? API_BASE.replace('/api/v1', '') : API_BASE;
  const [course, setCourse] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [currentLessonPath, setCurrentLessonPath] = useState(null);
  const [progress, setProgress] = useState({});
  const [lessonProgress, setLessonProgress] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});
  const [autoPlay, setAutoPlay] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCourseData = useCallback(async () => {
    try {
      setLoading(true);
      const [courseRes, progressRes] = await Promise.all([
        getCourse(courseId),
        getVideoProgress(courseId).catch(err => {
          console.error('Error fetching progress:', err);
          return { data: { data: { progress: [] } } }; // Return empty progress on error
        })
      ]);

      setCourse(courseRes.data.course);

      // Convert progress array to object for easy lookup
      const progressMap = {};
      if (progressRes.data.data.progress) {
        progressRes.data.data.progress.forEach((p) => {
          progressMap[p.lessonId] = p;
        });
      }
      setProgress(progressMap);

      // Expand all modules and submodules by default
      const allExpanded = {};
      if (courseRes.data.course.modules) {
        courseRes.data.course.modules.forEach(m => {
          allExpanded[m._id] = true;
          if (m.subModules) {
            m.subModules.forEach(sm => {
              allExpanded[`${m._id}.${sm._id}`] = true;
            });
          }
        });
      }
      setExpandedModules(allExpanded);

      // Auto-select first video lesson if none selected
      if (!currentLesson && courseRes.data.course.modules?.length > 0) {
        selectFirstVideoLesson(courseRes.data.course.modules);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching course data:', err);
      setError('Failed to load course');
      setLoading(false);
    }
  }, [courseId, currentLesson]);

  const fetchLessonProgress = useCallback(async () => {
    try {
      const res = await getVideoLessonProgress(courseId, currentLessonPath);
      setLessonProgress(res.data.data);
    } catch (err) {
      console.error('Error fetching lesson progress:', err);
      setLessonProgress(null);
    }
  }, [courseId, currentLessonPath]);

  // Fetch course and progress
  useEffect(() => {
    fetchCourseData();
  }, [fetchCourseData]);

  // Fetch lesson progress when lesson changes
  useEffect(() => {
    if (currentLessonPath && courseId) {
      fetchLessonProgress();
    }
  }, [currentLessonPath, courseId, fetchLessonProgress]);

  const selectFirstVideoLesson = (modules) => {
    for (let i = 0; i < modules.length; i++) {
      const module = modules[i];

      // Check direct lessons
      if (module.lessons?.length > 0) {
        const videoLesson = module.lessons.find(l => l.type === 'video');
        if (videoLesson) {
          const lessonPath = `${module._id}.${videoLesson._id}`;
          setCurrentLesson(videoLesson);
          setCurrentLessonPath(lessonPath);
          // No need to set expanded here as we expand all by default now
          return;
        }
      }

      // Check submodules
      if (module.subModules?.length > 0) {
        for (let j = 0; j < module.subModules.length; j++) {
          const subModule = module.subModules[j];
          if (subModule.lessons?.length > 0) {
            const videoLesson = subModule.lessons.find(l => l.type === 'video');
            if (videoLesson) {
              const lessonPath = `${module._id}.${subModule._id}.${videoLesson._id}`;
              setCurrentLesson(videoLesson);
              setCurrentLessonPath(lessonPath);
              return;
            }
          }
        }
      }
    }
  };

  const handleLessonSelect = (lesson, lessonPath) => {
    if (lesson.type === 'video') {
      setCurrentLesson(lesson);
      setCurrentLessonPath(lessonPath);
    }
  };

  const handleProgressUpdate = async (progressData) => {
    try {
      await saveVideoProgress({
        courseId,
        lessonId: currentLessonPath,
        ...progressData
      });

      // Update local progress
      setProgress((prev) => ({
        ...prev,
        [currentLessonPath]: {
          ...prev[currentLessonPath],
          ...progressData
        }
      }));
    } catch (err) {
      console.error('Error saving progress:', err);
    }
  };

  const handleLessonComplete = async () => {
    try {
      await markVideoLessonComplete(courseId, currentLessonPath);

      // Update local progress
      setProgress((prev) => ({
        ...prev,
        [currentLessonPath]: {
          ...prev[currentLessonPath],
          completed: true
        }
      }));

      // Auto-play next lesson if enabled
      if (autoPlay) {
        playNextLesson();
      }
    } catch (err) {
      console.error('Error marking lesson complete:', err);
    }
  };

  const playNextLesson = () => {
    if (!course || !currentLesson) return;

    let foundCurrent = false;
    let nextLesson = null;
    let nextLessonPath = null;

    // Helper to traverse and find next
    const traverse = () => {
      for (const module of course.modules) {
        // Check direct lessons
        if (module.lessons) {
          for (const lesson of module.lessons) {
            if (lesson.type !== 'video') continue;

            if (foundCurrent) {
              nextLesson = lesson;
              nextLessonPath = `${module._id}.${lesson._id}`;
              return true; // Found next
            }

            if (lesson._id === currentLesson._id) {
              foundCurrent = true;
            }
          }
        }

        // Check submodules
        if (module.subModules) {
          for (const subModule of module.subModules) {
            if (subModule.lessons) {
              for (const lesson of subModule.lessons) {
                if (lesson.type !== 'video') continue;

                if (foundCurrent) {
                  nextLesson = lesson;
                  nextLessonPath = `${module._id}.${subModule._id}.${lesson._id}`;
                  return true;
                }

                if (lesson._id === currentLesson._id) {
                  foundCurrent = true;
                }
              }
            }
          }
        }
      }
      return false;
    };

    traverse();

    if (nextLesson && nextLessonPath) {
      console.log('Playing next lesson:', nextLesson.title);
      handleLessonSelect(nextLesson, nextLessonPath);
    } else {
      console.log('No next lesson found or course completed');
    }
  };

  const toggleModule = (moduleId) => {
    setExpandedModules((prev) => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  const toggleSubModule = (moduleId, subModuleId) => {
    const key = `${moduleId}.${subModuleId}`;
    setExpandedModules((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const getLessonStatus = (lessonPath) => {
    const lessonProg = progress[lessonPath];
    if (!lessonProg) return 'locked';
    if (lessonProg.completed) return 'completed';
    if (lessonProg.watchedDuration > 0) return 'watching';
    return 'locked';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <i className="fas fa-check-circle text-success"></i>;
      case 'watching':
        return <i className="fas fa-play-circle text-primary"></i>;
      default:
        return <i className="fas fa-lock text-muted"></i>;
    }
  };

  const calculateCourseProgress = () => {
    if (!course || !course.modules) return 0;
    let totalLessons = 0;
    let completedLessons = 0;

    course.modules.forEach((module) => {
      if (module.lessons) {
        module.lessons.forEach((lesson) => {
          if (lesson.type === 'video') {
            totalLessons++;
            const lessonPath = `${module._id}.${lesson._id}`;
            if (progress[lessonPath]?.completed) {
              completedLessons++;
            }
          }
        });
      }

      if (module.subModules) {
        module.subModules.forEach((subModule) => {
          if (subModule.lessons) {
            subModule.lessons.forEach((lesson) => {
              if (lesson.type === 'video') {
                totalLessons++;
                const lessonPath = `${module._id}.${subModule._id}.${lesson._id}`;
                if (progress[lessonPath]?.completed) {
                  completedLessons++;
                }
              }
            });
          }
        });
      }
    });

    return totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;
  };

  if (loading) {
    return <div className="loading-container">Loading course...</div>;
  }

  if (error) {
    return <div className="error-container">{error}</div>;
  }

  if (!course) {
    return <div className="error-container">Course not found</div>;
  }

  const courseProgress = calculateCourseProgress();

  return (
    <div className="course-player-page">
      <div className="course-player-header">
        <button className="btn-back" onClick={() => {
          if (location.state?.returnUrl) {
            navigate(location.state.returnUrl);
          } else if (course.category === 'tradingview') {
            navigate('/video-courses?category=tradingview');
          } else if (course.category === 'indicator') {
            navigate('/sistem/indicator'); // Default back for indicator courses
          } else if (course.category === 'general') {
            navigate('/video-courses?category=general');
          } else {
            navigate('/student/my-courses');
          }
        }}>
          <i className="fas fa-arrow-left"></i> {location.state?.returnUrl ? 'Back' : 'Back to My Courses'}
        </button>
        <h1>{course.title}</h1>
        <div className="course-progress-bar">
          <div className="progress">
            <div
              className="progress-bar bg-success"
              style={{ width: `${courseProgress}%` }}
            >
              {courseProgress}%
            </div>
          </div>
        </div>
      </div>

      <div className="course-player-container">
        <div className="video-section">
          {currentLesson ? (
            <>
              {console.log('Current lesson:', currentLesson)}
              <VideoPlayer
                videoUrl={currentLesson.videoUrl && currentLesson.videoUrl.startsWith('http')
                  ? currentLesson.videoUrl
                  : `${ASSET_BASE}${currentLesson.videoUrl}`}
                lessonId={currentLessonPath}
                courseId={courseId}
                onProgressUpdate={handleProgressUpdate}
                initialProgress={lessonProgress}
                onComplete={handleLessonComplete}
              />
              <div className="lesson-info">
                <h2>{currentLesson.title}</h2>
                {currentLesson.description && <p>{currentLesson.description}</p>}
                <div className="lesson-actions">
                  <button
                    className="btn btn-success"
                    onClick={handleLessonComplete}
                  >
                    <i className="fas fa-check"></i> Mark as Complete
                  </button>
                  <label className="auto-play-toggle">
                    <input
                      type="checkbox"
                      checked={autoPlay}
                      onChange={(e) => setAutoPlay(e.target.checked)}
                    />
                    Auto-play next lesson
                  </label>
                </div>
              </div>
            </>
          ) : (
            <div className="no-lesson-selected">
              <i className="fas fa-video fa-3x"></i>
              <p>Select a lesson to start learning</p>
            </div>
          )}
        </div>

        <div className="lessons-sidebar">
          <h3>Course Content</h3>
          <div className="modules-list">
            {course.modules?.map((module, moduleIndex) => (
              <div key={module._id} className="module-item">
                <div
                  className="module-header"
                  onClick={() => toggleModule(module._id)}
                  style={{ backgroundColor: '#ff0000', padding: '1rem', color: 'white', borderRadius: '4px' }}
                >
                  <i
                    className={`fas fa-chevron-${expandedModules[module._id] ? 'down' : 'right'
                      }`}
                    style={{ color: 'white' }}
                  ></i>
                  <span className="module-title" style={{ color: 'white' }}>
                    {moduleIndex + 1}. {module.title}
                  </span>
                </div>

                {expandedModules[module._id] && (
                  <div className="module-content">
                    {/* Direct lessons */}
                    {module.lessons?.map((lesson, lessonIndex) => {
                      const lessonPath = `${module._id}.${lesson._id}`;
                      const status = getLessonStatus(lessonPath);
                      const isActive = currentLessonPath === lessonPath;

                      return (
                        <div
                          key={lesson._id}
                          className={`lesson-item ${isActive ? 'active' : ''}`}
                          onClick={() => handleLessonSelect(lesson, lessonPath)}
                        >
                          {getStatusIcon(status)}
                          <span className="lesson-title">
                            {lessonIndex + 1}. {lesson.title}
                          </span>
                          {lesson.duration && (
                            <span className="lesson-duration">
                              {Math.floor(lesson.duration / 60)}:{(lesson.duration % 60).toString().padStart(2, '0')}
                            </span>
                          )}
                        </div>
                      );
                    })}

                    {/* Submodules */}
                    {module.subModules?.map((subModule, subModuleIndex) => (
                      <div key={subModule._id} className="submodule-item">
                        <div
                          className="submodule-header"
                          onClick={() => toggleSubModule(module._id, subModule._id)}
                        >
                          <i
                            className={`fas fa-chevron-${expandedModules[`${module._id}.${subModule._id}`]
                              ? 'down'
                              : 'right'
                              }`}
                          ></i>
                          <span className="submodule-title">
                            {moduleIndex + 1}.{subModuleIndex + 1} {subModule.title}
                          </span>
                        </div>

                        {expandedModules[`${module._id}.${subModule._id}`] && (
                          <div className="submodule-lessons">
                            {subModule.lessons?.map((lesson, lessonIndex) => {
                              const lessonPath = `${module._id}.${subModule._id}.${lesson._id}`;
                              const status = getLessonStatus(lessonPath);
                              const isActive = currentLessonPath === lessonPath;

                              return (
                                <div
                                  key={lesson._id}
                                  className={`lesson-item ${isActive ? 'active' : ''}`}
                                  onClick={() => handleLessonSelect(lesson, lessonPath)}
                                >
                                  {getStatusIcon(status)}
                                  <span className="lesson-title">
                                    {lessonIndex + 1}. {lesson.title}
                                  </span>
                                  {lesson.duration && (
                                    <span className="lesson-duration">
                                      {Math.floor(lesson.duration / 60)}:{(lesson.duration % 60).toString().padStart(2, '0')}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoursePlayer;
